import Vue from 'vue'
import VueApollo from 'vue-apollo'
import ApolloClient from 'apollo-client'
import { createUploadLink } from 'apollo-upload-client'
import {
  InMemoryCache,
  IntrospectionFragmentMatcher
} from 'apollo-cache-inmemory'
import fragmentTypes from './graphql/fragmentTypes.json'
import { onError } from 'apollo-link-error'
import { ApolloLink, fromPromise } from 'apollo-link'
import { clearLegacyAuthCookies } from '@/utils/cookies'
import { tokenStore } from '@/utils/tokenStore'
import { RefreshAccessTokenQuery } from './types/graphql'
import router from './router'
import PusherLink from './graphql/links/pusher.link'

Vue.use(VueApollo)

// Auth moved to HttpOnly cookies set by the backend; drop any tokens the old
// JS-cookie implementation left behind.
clearLegacyAuthCookies()

let apolloClient: any = null
const echoLink = new PusherLink() as any
const httpEndpoint =
  process.env.VUE_APP_GRAPHQL_HTTP || 'http://localhost:4000/graphql'

const httpOptions = {
  uri: httpEndpoint,
  // Send the HttpOnly auth cookies with every request.
  credentials: 'include'
}

const currentEnvMode = process.env.VUE_APP_ENV

const httpLink = createUploadLink(httpOptions) as any

const logLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    if (currentEnvMode === 'local') {
      const operationType = (
        operation.query?.definitions &&
        operation.query?.definitions.length
      )
        ? (operation.query?.definitions[0] as any).operation
        : null

      console.debug(`%c🚀 [GraphQL Request] %c'${operation.operationName}' ${operationType.toUpperCase()}\n`, 'color: #1E88E5; font-weight: bold', 'color: #1976D2; font-weight: bold', operation.variables, '\n', response.data, '\n\n') // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring
    }
    return response
  })
})

let refreshTokenPromise: Promise<string> | null = null

const getNewToken = async() => {
  if (!apolloClient) throw new Error('Apollo-Client is not found! ')

  if (!refreshTokenPromise) {
    // No refresh_token in the body; the backend reads it from the HttpOnly
    // cookie and sets the rotated cookie pair on the response.
    refreshTokenPromise = apolloClient
      .mutate({
        mutation: RefreshAccessTokenQuery,
        variables: { input: {} }
      })
      .then((response: any) => {
        const { access_token } = response.data.refreshToken
        tokenStore.set(access_token)
        return access_token
      })
      .finally(() => {
        refreshTokenPromise = null // Reset the promise after it resolves
      })
  }

  return refreshTokenPromise
}

const errorLink = onError(
  ({ graphQLErrors, operation, forward, networkError }) => {
    if (networkError) {
      if ('statusCode' in networkError && networkError.statusCode === 503) {
        const endpoint = httpEndpoint.replace(/\/graphql$/, '')

        window.location.href = `${endpoint}/maintenance?redirect_url=${window.location.href}`
      }
    }

    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        console.error(`%c🚀 [GraphQL ERROR] %c'${operation.operationName}'\n`, 'color: #1E88E5; font-weight: bold', 'color: #1976D2; font-weight: bold', err, '\n\n') // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring

        switch (err.message) {
          case 'Not Authenticated':
            router.push({ name: 'login', query: { logout: '1', redirect: router.currentRoute.path } })
            break
          case 'Unauthenticated.':
            // The refresh token lives in an HttpOnly cookie JS cannot check;
            // always attempt the refresh and treat failure as a dead session.
            return fromPromise(
              getNewToken().catch(async(error: any) => {
                console.error(error)

                if (!router.currentRoute.path.startsWith('/tracking')) {
                  router.push({ name: 'login', query: { logout: '1', redirect: router.currentRoute.path } })
                }

                // Re-throw so fromPromise emits an error rather than completing
                // empty — an empty observable leaves the caller's promise pending
                // forever (finally never runs, loading states never clear).
                throw error
              })
            )
              .flatMap(() => {
                // Retry the request: the rotated HttpOnly cookie set by the
                // refresh response authenticates it.
                return forward(operation)
              })
        }
      }
    }
  }
)
const link = ApolloLink.from([errorLink, httpLink])

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData: fragmentTypes
  // fragmentTypes
})

apolloClient = new ApolloClient({
  link: ApolloLink.from([logLink, echoLink, link]),
  cache: new InMemoryCache({
    fragmentMatcher,
    addTypename: true
  }),
  defaultOptions: {
    query: {
      // https://github.com/apollographql/apollo-client/issues/6760#issuecomment-668188727
      fetchPolicy: 'cache-first'
    }
  }
})

// Call this in the Vue app file
export function createProvider() {
  if (!apolloClient) throw new Error('Apollo-Client is not found! ')
  const apolloProvider = new VueApollo({
    defaultClient: apolloClient,
    defaultOptions: {},
    errorHandler(error) {
      // eslint-disable-next-line no-console
      console.log(
        '%cError',
        'background: red; color: white; padding: 2px 4px; border-radius: 3px; font-weight: bold;',
        error.message
      )
    }
  })

  return apolloProvider
}
